import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  Ref,
  useImperativeHandle,
} from "react";
import _ from "lodash";
import classnames from "classnames/bind";
import {
  IMake,
  IModel,
  ISearchVehicle,
  IVehicleCategory,
  IVehicleShort,
  Replacement,
} from "../../../../interface";
import VehicleList from "../VehicleList";
import FilterDropdown from "../../FilterDropdown";
import FilterResult from "../../FilterResult";
import ToggleFilter from "../../../ToggleFilter";
import style from "./index.module.scss";
import instance from "../../../../services/httpRequest";
import analyseData from "../../../../utils/analyseSearchData";
import Loading from "../../../Loading";
import localesReplacement from "../../../../utils/localesReplacement";
import ClickOut from "react-click-out";
import {getTypeFromId} from "../../../../utils/matchVehicleCategories";
import {Dispatch} from "redux";
import actions from "../../../../redux/actions";
import {connect} from "react-redux";
import Filter from "../../../../utils/filter";
import {useSearchParams, useNavigate} from "react-router-dom";
import {useAppName} from "../../../../hooks/use-app-names";
import {useAnalytics} from "../../../../hooks/use-analytics";

import {
  APPNAME,
  DURATION,
  FASTSCAN,
  ANALYTICS_EVENT,
  ANALYTICS_PAGE_TYPE,
  ANALYTICS_SEARCH_TYPE,
  ANALYTICS_USER_TYPE,
  BACK_BUTTON_ENABLED_COUNTRIES,
  YEAR_API_CALL_ENABLED_COUNTRIES,
  YEAR_MANDATORY_COUNTRIES,
} from "../../../../constants";

import {useMediaQuery} from "react-responsive";
import backIcon from "../../../../images/icon-back-primary.svg";

export interface VehicleSearchRef {
  clearValue: () => void;
}
interface IProps {
  country?: string;
  replacements: Replacement;
  onVehicleSelect?: (
    value: string | number,
    model: string,
    vehicleType?: string
  ) => void;
  categoriesType: string;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
  setResults: (v: ISearchVehicle[] | null) => void;
  setLoading: (v: boolean) => void;
  setLicenseKeys: (v: string) => void;
  searchHandler: () => void;
  quickSearch: any;
}

const cx = classnames.bind(style);
const classDropdownItem = "dropdown-item";

const VehicleSearch = forwardRef(function VehicleSearch(
  props: IProps,
  ref: Ref<VehicleSearchRef>
) {
  const {
    replacements,
    onVehicleSelect,
    categoriesType,
    selectedWorkshop,
    restore,
    preserved,
    setResults,
    setLicenseKeys,
    setLoading,
    searchHandler,
    quickSearch,
    country,
  } = props;
  const navigate = useNavigate();
  const isFastScan = useAppName(FASTSCAN);
  const sendAnalytics = useAnalytics();
  const isDesktopOrLaptop = useMediaQuery({minWidth: 1024});
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicleTypes, setVehicleTypes] = useState<IVehicleCategory[]>();
  const [typeID, setTypeID] = useState<number | string>();
  const [makes, setMakes] = useState<IMake[]>([]);
  const [make, setMake] = useState<string>("");
  const [models, setModels] = useState<IModel[]>([]);
  const [model, setModel] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [years, setYears] = useState<string[]>([]);
  const [isYearVisible, setIsYearVisible] = useState<boolean>(false);
  const [isMakeVisible, setIsMakeVisible] = useState<boolean>(false);
  const [isModelVisible, setIsModelVisible] = useState<boolean>(false);
  const [vehicles, setVehicles] = useState<IVehicleShort[]>([]);
  const [filterVisible, setFilterVisible] = useState<boolean>(true);
  const [isMount, setIsMount] = useState<boolean>(false);
  const type = _.chain(vehicleTypes).find(["id", typeID]).get("name").value();
  const controller = new AbortController();

  const modelMapper = (model: any): string => {
    const name = model.name || model.model || "Unknown model";
    const yearFrom = model.yearFrom;
    const yearTo = model.yearTo;

    let years = "";

    if (yearFrom && yearTo) {
      years = `(${yearFrom} - ${yearTo})`;
    } else if (yearFrom && !yearTo) {
      years = `(${yearFrom} - )`;
    } else if (!yearFrom && yearTo) {
      years = `( - ${yearTo})`;
    }

    return `${name} ${years}`.trim();
  };

  const brazilYears = Array.from({length: 2023 - 2009 + 1}, (_, i) =>
    (2009 + i).toString()
  );

  const memoDate = useCallback(() => {
    setIsMount(true);
    selectedWorkshop
      ? _.assign(instance.defaults.headers, {
          isWorkshopOwner: selectedWorkshop,
        })
      : _.unset(instance.defaults.headers, "isWorkshopOwner");
    instance
      .get("/categories", {signal: controller.signal})
      .then((d) => {
        setVehicleTypes(d.data);
        const searchTypeId = sessionStorage.getItem("searchTypeId");
        const searchParamTypeId = searchParams.get("typeId");
        let typeId = null;
        if (country !== "BRA") {
          typeId = d.data.at(0).id;
          if (searchParamTypeId) {
            if (
              d.data.find(
                (item: any) => item.id === parseInt(searchParamTypeId)
              )
            ) {
              typeId = parseInt(searchParamTypeId);
            }
          } else if (d.data?.find((item: any) => searchTypeId === item.id)) {
            typeId = searchTypeId;
          }
        }
        setTypeID(typeId);
      })
      .then(() => {
        if (country === "BRA") {
          setYears(brazilYears);
        }
        setIsMount(false);
      })
      .catch(() => {
        sendAnalytics({
          event: ANALYTICS_EVENT.VEHICLE_SEARCH_UNAVAILABLE,
          page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
          search: ANALYTICS_SEARCH_TYPE.VEHICLE_SEARCH,
          user: selectedWorkshop
            ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER
            : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
          country: country,
        });
      });
  }, [selectedWorkshop, country]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLicenseKeys("");
    const fetchMakes = async () => {
      let url = `/makes?categoryId=${typeID}`;
      if (country === "BRA") {
        url += `&Year=${year}`;
        if (typeID === 1) {
          url += `&categoryId=${16}`;
        }
      }
      const response = await instance.get(url, {signal: controller.signal});
      if (isMounted) {
        setMakes(response.data);
        setIsMount(false);
      }
    };
    if (typeID) {
      fetchMakes();
    }
    return () => {
      isMounted = false;
    };
  }, [country, typeID, year]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (make) {
      const makeId = _.chain(makes).find(["name", make]).get("id");
      let url = `/models?makeId=${makeId}`;
      if (country === "BRA") {
        url += `&categoryId=${typeID}&Year=${year}`;
        if (typeID === 1) {
          url += `&VehicleType=Car&VehicleType=LightUtility`;
        }
      }
      instance
        .get(url, {signal: controller.signal})
        .then((d) => {
          if (d.data && d.data.length > 0) {
            setModels(d.data);
          } else {
            setModels([]);
          }
        })
        .catch((error) => {
          if (
            error.response &&
            error.response.status === 500 &&
            error.response.status == 403
          ) {
            setIsSubmitted(true);
          } else {
            console.error("Error fetching models:", error);
          }
          setModels([]);
        });
    }
  }, [make, year, country, makes]);

  useEffect(() => {
    if (!make) return;

    const makeId = _.chain(makes).find(["name", make]).get("id").value();
    if (!makeId) return;

    const mappedModels = models.map((m) => ({
      ...m,
      displayName: modelMapper({
        name: m.model,
        yearFrom: m.yearFrom,
        yearTo: m.yearTo,
      }),
    }));

    // Match model by displayName
    const selectedModel = mappedModels.find((m) => m.displayName === model);
    if (!selectedModel) return;
    let url;
    if (country === "BRA") {
      url = `/vehicleSearch?makeId=${makeId}&Year=${year}&categoryId=${typeID}`;
    } else {
      url =
        year && country && YEAR_API_CALL_ENABLED_COUNTRIES.includes(country)
          ? `/vehicleSearch?modelId=${selectedModel.id}&year=${year}`
          : `/vehicleSearch?modelId=${selectedModel.id}`;
    }
    model &&
      selectedModel &&
      instance.get(url, {signal: controller.signal}).then((d) => {
        const yrs = analyseData.year(d.data);
        if (country !== "BRA") {
          if (yrs.length === 0) {
            const yrs = analyseData.year(vehicles);
            setYears(yrs);
          } else {
            setYears(yrs);
          }
        }
        yrs.sort((a: any, b: any) => b - a);
        setVehicles(d.data);
      });
  }, [
    make,
    model,
    country && YEAR_API_CALL_ENABLED_COUNTRIES.includes(country) ? year : null,
    year,
  ]);

  const changeVehicleType = (id: number) => {
    setTypeID(id);
    setMakes([]);
    setMake("");
    setModels([]);
    setModel("");
    setVehicles([]);
    setYear("");
    restore && sessionStorage.setItem("searchTypeId", _.toString(id));
    restore && sessionStorage.removeItem("searchMake");
    restore && sessionStorage.removeItem("searchModel");
    restore && sessionStorage.removeItem("searchYear");
    reset();
    if (country === "BRA") {
      setIsYearVisible(true);
      setIsMakeVisible(false);
      setIsModelVisible(false);
    }
  };

  const changeMake = (make: string) => {
    setMake(make);
    setModels([]);
    setModel("");
    setVehicles([]);
    setYear("");
    restore && sessionStorage.setItem("searchMake", make);
    restore && sessionStorage.removeItem("searchModel");
    restore && sessionStorage.removeItem("searchYear");
    reset();
  };
  const changeModel = (model: string) => {
    setIsSubmitted(false);
    const vehicletype = vehicleTypes?.find((d) => d.id === typeID);
    setModel(model);
    setYear("");
    !model && setVehicles([]);
    restore && sessionStorage.setItem("searchModel", model);
    restore && sessionStorage.removeItem("searchYear");
    reset();
    sendAnalytics({
      event: ANALYTICS_EVENT.VEHICLE_SEARCH,
      page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
      search: ANALYTICS_SEARCH_TYPE.VEHICLE_SEARCH,
      user: selectedWorkshop
        ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER
        : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
      category: vehicletype?.name,
      make: make,
      model: model,
      country: country,
    });
  };
  const preservedRef = useRef<boolean>();
  useEffect(() => {
    memoDate();
    if (!preserved || _.isEqual(preservedRef.current, preserved)) {
      setMakes([]);
      setMake("");
      setModels([]);
      setModel("");
      setVehicles([]);
      setYear("");
      restore && sessionStorage.removeItem("searchTypeId");
      restore && sessionStorage.removeItem("searchMake");
      restore && sessionStorage.removeItem("searchModel");
      restore && sessionStorage.removeItem("searchYear");
    }
    preservedRef.current = preserved;
  }, [selectedWorkshop, preserved, country]);

  useEffect(() => {
    if (preserved) {
      sessionStorage.getItem("searchTypeId") &&
        setTypeID(
          _.toNumber(sessionStorage.getItem("searchTypeId")) ?? undefined
        );
    }
  }, [preserved]);
  useEffect(() => {
    if (preserved && !_.isEmpty(makes)) {
      sessionStorage.getItem("searchMake") &&
        setMake(sessionStorage.getItem("searchMake") ?? "");
    }
  }, [preserved, makes]);
  useEffect(() => {
    if (preserved && !_.isEmpty(models)) {
      sessionStorage.getItem("searchModel") &&
        setModel(sessionStorage.getItem("searchModel") ?? "");
    }
  }, [preserved, models]);
  useEffect(() => {
    if (preserved && !_.isEmpty(vehicles)) {
      sessionStorage.getItem("searchYear") &&
        setYear(sessionStorage.getItem("searchYear") ?? "");
    }
  }, [preserved, vehicles]);

  const [typeVisible, setTypeVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const searchResult = _.map(vehicles, (d) => {
    return {
      ...d,
      vehicleType: type,
      make: make,
      model: model,
    };
  });
  const timeoutRef = useRef<NodeJS.Timeout>();
  const resultRef = useRef<ISearchVehicle[]>();
  useEffect(() => {
    if (!isSubmitted) return;
    clearTimeout(timeoutRef.current);

    const filteredResults = new Filter(searchResult)
      .vehicleType(type)
      .manufacturer(make)
      .model(model)
      .year(year)
      .value();
    if ((!vehicles || _.isEmpty(vehicles)) && country !== "BRA") {
      setResults(null);
      if (make && model) {
        setLicenseKeys(
          `${localesReplacement(
            replacements,
            "No results found for"
          )} ${make} ${model} ${year}`
        );
      } else {
        setLicenseKeys("");
      }
    } else if (country === "BRA" && _.isEmpty(models)) {
      setResults(null);
      setLicenseKeys(
        `${localesReplacement(replacements, "No results found for")} ${make}`
      );
    } else {
      if (!_.isEmpty(filteredResults)) {
        setResults(filteredResults);
        resultRef.current = filteredResults;
        setLoading(true);
        timeoutRef.current = setTimeout(() => {
          setLoading(false);
        }, DURATION);
      } else {
        setResults(searchResult);
      }
    }
  }, [vehicles, model, year, isSubmitted, models, country, make]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      setLoading(false);
    };
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return {
        clearValue() {
          if (make) {
            setMakes([]);
            setMake("");
            setModels([]);
            setModel("");
            setVehicles([]);
            setYear("");
            instance
              .get(`/makes?categoryId=${typeID}`, {signal: controller.signal})
              .then((d) => {
                setMakes(d.data);
                setModels([]);
                setModel("");
                setVehicles([]);
                setYear("");
              });
          }
        },
      };
    },
    [vehicleTypes, typeID, make]
  );
  const reset = () => {
    searchHandler();
  };

  const searchDisabled = () => {
    if (YEAR_MANDATORY_COUNTRIES.includes(country ?? "GBR")) {
      return !(model && year);
    }
    return !model;
  };

  return (
    <>
      {!isMount ? (
        <>
          {!!vehicleTypes && (
            <div className={cx("vehicle-search")}>
              <div className={cx("dropdown-wrapper")}>
                <ClickOut
                  className="click-out"
                  onClickOut={() => setTypeVisible(false)}
                >
                  <div
                    className={cx(
                      "dropdown",
                      {on: typeVisible},
                      _.kebabCase(getTypeFromId(+(typeID ?? 0), categoriesType))
                    )}
                    onClick={() => setTypeVisible(!typeVisible)}
                  >
                    {!typeID ? (
                      localesReplacement(
                        replacements,
                        "Select your vehicle or equipment type"
                      )
                    ) : (
                      <>
                        <i></i>
                        <span className="overflowText">
                          {_.chain(vehicleTypes)
                            .find(["id", typeID])
                            .get("name")
                            .value()}
                        </span>
                      </>
                    )}
                  </div>
                </ClickOut>
                <div className={cx("mask", {on: typeVisible})}></div>
                <div className={cx("flyout", {on: typeVisible})}>
                  <VehicleList
                    dataList={vehicleTypes}
                    selected={typeID ?? 0}
                    changeVehicleTypeHandler={changeVehicleType}
                    categoriesType={categoriesType}
                  />
                </div>
              </div>
              <div className={cx("dropdown-list")}>
                {country === "BRA" ? (
                  <>
                    <div className={cx(classDropdownItem)}></div>
                    <div className={cx(classDropdownItem)}>
                      <FilterDropdown
                        dataList={years}
                        filter={year}
                        placeholder={localesReplacement(
                          replacements,
                          "Select year"
                        )}
                        changeHandler={(v) => {
                          setIsSubmitted(false);
                          setYear(v);
                          reset();
                          sessionStorage.setItem("searchYear", v);
                          setIsMakeVisible(true);
                        }}
                        type={localesReplacement(replacements, "Year")}
                        replacements={replacements}
                        disabled={!isYearVisible}
                      />
                    </div>
                    <div className={cx(classDropdownItem)}>
                      <FilterDropdown
                        dataList={makes}
                        filter={make}
                        placeholder={localesReplacement(
                          replacements,
                          "Type or select brand"
                        )}
                        changeHandler={(make) => {
                          setIsSubmitted(false);
                          setMake(make);
                          reset();
                          sessionStorage.setItem("searchMake", make);
                          setIsModelVisible(true);
                        }}
                        type={localesReplacement(replacements, "Make")}
                        replacements={replacements}
                        disabled={!isMakeVisible}
                      />
                    </div>
                    <div className={cx(classDropdownItem)}>
                      <FilterDropdown
                        dataList={_.chain(models)
                          .map((d) => {
                            return {
                              id: d["id"],
                              name: d["model"],
                              yearFrom: d["yearFrom"],
                              yearTo: d["yearTo"],
                              DisplayName: d["DisplayName"],
                            };
                          })
                          .map(modelMapper)
                          .value()}
                        filter={model}
                        placeholder={localesReplacement(
                          replacements,
                          "Type or select model"
                        )}
                        changeHandler={(model) => {
                          setIsSubmitted(false);
                          setModel(model);
                          reset();
                          sessionStorage.setItem("searchModel", model);
                          setIsYearVisible(true);
                        }}
                        type={localesReplacement(replacements, "Model")}
                        replacements={replacements}
                        disabled={!isModelVisible}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className={cx(classDropdownItem)}>
                      <FilterDropdown
                        dataList={makes}
                        filter={make}
                        placeholder={localesReplacement(
                          replacements,
                          "Type or select brand"
                        )}
                        changeHandler={changeMake}
                        type={localesReplacement(replacements, "Make")}
                        replacements={replacements}
                      />
                    </div>
                    <div className={cx(classDropdownItem)}>
                      <FilterDropdown
                          dataList={_.chain(models)
                            .thru(arr => country === "USA" ? _.sortBy(arr, (d) => d.yearFrom) : arr)
                          .map((d) => {
                            return {
                              id: d["id"],
                              name: d["model"],
                              yearFrom: d["yearFrom"],
                              yearTo: d["yearTo"],
                            };
                          })
                          .map(modelMapper)
                          .value()}
                        filter={model}
                        placeholder={localesReplacement(
                          replacements,
                          "Type or select model"
                        )}
                        changeHandler={changeModel}
                        type={localesReplacement(replacements, "Model")}
                        replacements={replacements}
                      />
                    </div>
                    <div hidden>
                      <ToggleFilter
                        toggleHandler={setFilterVisible}
                        panelVisible={filterVisible}
                      />
                    </div>
                    <div
                      className={`yearDropdown ${cx(
                        classDropdownItem,
                        "yearDropdown",
                        {
                          hidden: !filterVisible,
                        }
                      )}`}
                    >
                      <FilterDropdown
                        dataList={years}
                        filter={year}
                        placeholder={localesReplacement(
                          replacements,
                          "Select year"
                        )}
                        changeHandler={(v) => {
                          setIsSubmitted(false);
                          setYear(v);
                          reset();
                          sessionStorage.setItem("searchYear", v);
                        }}
                        type={localesReplacement(replacements, "Year")}
                        replacements={replacements}
                      />
                    </div>
                  </>
                )}
              </div>

              <button
                className={cx("search-btn")}
                disabled={searchDisabled()}
                onClick={() => {
                  if (!isSubmitted) {
                    setIsSubmitted(false);
                    setTimeout(() => {
                      setIsSubmitted(true);
                    }, 0);
                    searchHandler();
                    if (isDesktopOrLaptop) {
                      searchParams.delete("id");
                      searchParams.set("page", "landing");
                      isFastScan && searchParams.set(APPNAME, FASTSCAN);
                      setSearchParams(searchParams);
                    }
                  }
                }}
              >
                {localesReplacement(replacements, "Search")}
              </button>
            </div>
          )}
          {searchParams.get("page") === "recommendedProducts" &&
            searchResult &&
            BACK_BUTTON_ENABLED_COUNTRIES.includes(country || "USA") && (
              <button
                className={cx("back-btn")}
                onClick={() => {
                  if (quickSearch.current.value.trim()) {
                    const val = quickSearch.current.value.trim();
                    quickSearch.current.clearValue();
                    setTimeout(() => {
                      quickSearch.current.set(val + " ");
                    }, 0);
                    return;
                  }
                  setIsSubmitted(false);
                  navigate(-1);
                  setTimeout(() => {
                    setIsSubmitted(true);
                  }, 0);
                }}
              >
                <img src={backIcon} />
                <span>{localesReplacement(replacements, "Back")}</span>
              </button>
            )}
          {isSubmitted && (
            <FilterResult
              dataList={searchResult}
              vehicleType={type}
              make={make}
              model={model}
              year={year}
              onSelect={onVehicleSelect}
              replacements={replacements}
              isBrazil={false}
            />
          )}
        </>
      ) : (
        <Loading />
      )}
    </>
  );
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setResults: (vehicle: ISearchVehicle[] | null) =>
    dispatch(actions.setResults(vehicle)),
  setLoading: (v: boolean) => dispatch(actions.setLoading(v)),
  setLicenseKeys: (v: string) => dispatch(actions.setLicenseKeys(v)),
});

export default connect(null, mapDispatchToProps, null, {forwardRef: true})(
  VehicleSearch
);
